import React, { useState, useEffect } from "react";
import { RefreshCw, Mail, Printer } from "lucide-react";
import Tooltip from "../../../../../components/Tooltip/Tooltip";
import ExportMenu, { type ExportFormat } from "./ExportMenu";
import MoreActionsMenu from "./MoreActionsMenu";
import CompactViewMenu, { type Density, type Zoom } from "./CompactViewMenu";

interface Props {
  onRefresh: () => void;
  onEmail: () => void;
  onPrint: () => void;
  onExport: (format: ExportFormat) => void;
  onScheduleReport: () => void;
  onShareReport: () => void;
  onAddToGroup: () => void;
  onAddToManagementReports: () => void;
  lastRefreshedAt: number | null;
  refreshing: boolean;
  density: Density;
  zoom: Zoom;
  groupsExpanded: boolean;
  onDensityChange: (d: Density) => void;
  onZoomChange: (z: Zoom) => void;
  onGroupsExpandedChange: (expanded: boolean) => void;
}

function formatElapsed(ts: number | null): string {
  if (ts === null) return "Refresh";
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 5) return "Last refreshed just now";
  if (diff < 60) return `Last refreshed ${diff} seconds ago`;
  const mins = Math.floor(diff / 60);
  if (mins < 60) return `Last refreshed ${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Last refreshed ${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `Last refreshed ${days} day${days === 1 ? "" : "s"} ago`;
}

export default function QuickReportToolbar({
  onRefresh, onEmail, onPrint, onExport, onScheduleReport, onShareReport, onAddToGroup, onAddToManagementReports,
  lastRefreshedAt, refreshing, density, zoom, groupsExpanded, onDensityChange, onZoomChange, onGroupsExpandedChange,
}: Props) {
  const [, setTick] = useState(0);

  // Re-render every 10s so the tooltip elapsed time stays fresh
  useEffect(() => {
    if (lastRefreshedAt === null) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 10000);
    return () => window.clearInterval(id);
  }, [lastRefreshedAt]);

  return (
    <div className="flex items-center justify-between py-2.5 border-b border-[#EEF1F4]">
      <CompactViewMenu
        density={density}
        zoom={zoom}
        groupsExpanded={groupsExpanded}
        onDensityChange={onDensityChange}
        onZoomChange={onZoomChange}
        onGroupsExpandedChange={onGroupsExpandedChange}
      />
      <div className="flex gap-0.5">
        <Tooltip label={formatElapsed(lastRefreshedAt)}>
          <IconBtn onClick={onRefresh}>
            <RefreshCw
              className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
              style={refreshing ? { animationIterationCount: "1", animationDuration: "0.45s" } : undefined}
            />
          </IconBtn>
        </Tooltip>
        <Tooltip label="Email"><IconBtn onClick={onEmail}><Mail className="w-4 h-4" /></IconBtn></Tooltip>
        <Tooltip label="Print"><IconBtn onClick={onPrint}><Printer className="w-4 h-4" /></IconBtn></Tooltip>
        <ExportMenu onExport={onExport} />
        <MoreActionsMenu
          onShareReport={onShareReport}
          onScheduleReport={onScheduleReport}
          onAddToGroup={onAddToGroup}
          onAddToManagementReports={onAddToManagementReports}
        />
      </div>
    </div>
  );
}

function IconBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return <button type="button" onClick={onClick} className="w-8 h-8 rounded-md inline-flex items-center justify-center text-ink2 hover:bg-[#EEF4FB] hover:text-ink">{children}</button>;
}